<template>
  <div class="pl" :class="'is-' + phase">
    <div class="pl__bg" aria-hidden="true">
      <div class="pl__glow pl__glow--1" />
      <div class="pl__glow pl__glow--2" />
      <div class="pl__ring" :class="{ 'is-hot': phase === 'pending' }" />
    </div>

    <div class="pl__head">
      <div class="pl__banner">{{ confirmed ? '中奖揭晓' : '照片抽奖' }}</div>
      <!-- 揭晓时奖品名在卡片内展示，避免与卡片重叠 -->
      <div v-if="prizeName && !confirmed" class="pl__prize-name">{{ prizeName }}</div>
    </div>

    <!-- 照片球：待机慢转；抽奖中整体放大、加速旋转，直到控台确认 -->
    <div
      v-show="phase === 'idle' || phase === 'pending'"
      class="pl__persp"
      :class="{ 'is-fast': phase === 'pending' }"
    >
      <div class="pl__world">
        <div
          v-for="item in items"
          :key="item.id"
          class="pl__slot"
          :style="item.style"
        >
          <div class="pl__card">
            <img :src="item.photo" alt="">
            <div class="pl__nick">{{ item.nick }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="phase === 'pending'" class="pl__drawing">
      <div class="pl__drawing-text">抽奖中</div>
    </div>

    <div v-else-if="phase === 'idle'" class="pl__wait">
      <p v-if="hasPhotos">主持人即将开始抽奖</p>
      <p v-else>等待大家上传照片</p>
    </div>

    <!-- 确认后：居中展示中奖者照片 -->
    <div v-if="confirmed && winner" class="pl__reveal">
      <div class="pl__winner">
        <div class="pl__winner-label">恭喜中奖</div>
        <img v-if="winnerPhoto" :src="winnerPhoto" alt="">
        <div class="pl__winner-nick">{{ winner.nick || winner.uid }}</div>
        <div v-if="prizeName" class="pl__winner-prize">{{ prizeName }}</div>
      </div>
    </div>

    <div class="pl__foot">已入场 {{ onlineCount || 0 }} 人 · 已上传 {{ photos.length }} 张照片</div>
  </div>
</template>

<script>
export default {
  name: 'PhotoLotteryStage',
  props: {
    photos: { type: Array, default: () => [] },
    draft: { type: Object, default: null },
    h5: { type: Object, default: () => ({}) },
    onlineCount: { type: Number, default: 0 }
  },
  data() {
    return {
      items: [],
      sphereRadius: 420,
      lastPhotoKey: ''
    }
  },
  computed: {
    phase() {
      const status = (this.draft && this.draft.status) || ''
      if (status === 'confirmed') return 'reveal'
      if (status === 'pending') return 'pending'
      return 'idle'
    },
    confirmed() {
      return !!(this.draft && this.draft.status === 'confirmed' && this.draft.winner)
    },
    winner() {
      return (this.draft && this.draft.winner) || null
    },
    /** 中奖者只展示本人上传的照片，不用头像兜底 */
    winnerPhoto() {
      const w = this.winner || {}
      return w.photo || ''
    },
    prizeName() {
      return (this.draft && this.draft.prizeName) || ''
    },
    hasPhotos() {
      return !!(this.photos && this.photos.length)
    },
    /** 抽奖中：球体半径放大，旋转范围更大 */
    radiusScale() {
      return this.phase === 'pending' ? 1.3 : 1
    },
    /** 照片球重建依据：人数或照片地址任一变化（同一用户重新上传也要刷新） */
    photoKey() {
      const list = this.photos || []
      if (!list.length) return ''
      return list.map(p => String(p.uid) + ':' + String((p && (p.photo || p.avatar)) || '')).join('|')
    }
  },
  watch: {
    photoKey: {
      immediate: true,
      handler() {
        this.rebuildItems()
      }
    },
    sphereRadius() {
      this.rebuildItems()
    },
    radiusScale() {
      this.rebuildItems()
    }
  },
  mounted() {
    this.refreshRadius()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize() {
      this.refreshRadius()
    },
    refreshRadius() {
      const w = window.innerWidth || 1920
      const h = window.innerHeight || 1080
      const next = Math.round(Math.min(w, h) * 0.4)
      if (next !== this.sphereRadius) this.sphereRadius = next
    },
    rebuildItems() {
      const source = (this.photos || []).filter(p => p && (p.photo || p.avatar))
      const list = source.slice(0, 48)
      const n = list.length ? Math.max(list.length, 18) : 0
      const out = []
      for (let i = 0; i < n; i++) {
        // 照片少时重复铺满球体；key 用序号保证唯一
        const p = list[i % list.length]
        const R = this.sphereRadius * this.radiusScale
        const y = 1 - (i / Math.max(1, n - 1)) * 2
        const radius = Math.sqrt(Math.max(0, 1 - y * y))
        const theta = Math.PI * (3 - Math.sqrt(5)) * i
        const x = Math.cos(theta) * radius
        const z = Math.sin(theta) * radius
        out.push({
          id: 'p' + i,
          photo: p.photo || p.avatar,
          nick: p.nick || '',
          style: {
            transform: 'translate3d(' + (x * R) + 'px,' + (y * R) + 'px,' + (z * R) + 'px)'
          }
        })
      }
      this.items = out
    }
  }
}
</script>

<style scoped>
.pl {
  position: absolute;
  inset: 0;
  overflow: hidden;
  color: #fff;
  --pl-spin: 20s;
}
.pl__bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 45%, #221046 0%, #0a0418 62%, #04010c 100%);
  overflow: hidden;
}
.pl__glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.55;
}
.pl__glow--1 {
  width: 42vw;
  height: 42vw;
  left: 8%;
  top: 10%;
  background: rgba(123, 60, 255, 0.5);
  animation: plFloat 9s ease-in-out infinite alternate;
}
.pl__glow--2 {
  width: 36vw;
  height: 36vw;
  right: 6%;
  bottom: 8%;
  background: rgba(200, 75, 255, 0.42);
  animation: plFloat 11s ease-in-out infinite alternate-reverse;
}
.pl__ring {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 62vw;
  height: 62vw;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 1px solid rgba(200, 160, 255, 0.28);
  box-shadow: inset 0 0 8vw rgba(140, 80, 255, 0.22);
}
.pl__head {
  position: relative;
  z-index: 20;
  padding-top: 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
.pl__banner {
  display: inline-block;
  padding: 8px 36px;
  border-radius: 24px;
  background: linear-gradient(90deg, #7b3cff, #c84bff);
  font-size: 28px;
  letter-spacing: 4px;
  box-shadow: 0 0 24px rgba(160, 80, 255, 0.55);
}
.pl__prize-name {
  display: inline-block;
  margin-top: 0;
  padding: 8px 30px;
  border-radius: 999px;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #ffe6a0;
  background: rgba(12, 8, 26, 0.62);
  border: 1px solid rgba(255, 214, 102, 0.55);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.45), 0 0 18px rgba(255, 200, 70, 0.25);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.9);
}
.pl__persp {
  position: absolute;
  inset: 0;
  z-index: 2;
  perspective: 1200px;
  pointer-events: none;
}
.pl__persp.is-fast {
  --pl-spin: 1.2s;
}
.pl__world {
  position: absolute;
  left: 50%;
  top: 52%;
  width: 0;
  height: 0;
  transform-style: preserve-3d;
  animation: plWorldSpin var(--pl-spin) linear infinite;
}
.pl__slot {
  position: absolute;
  left: 0;
  top: 0;
  transform-style: preserve-3d;
  /* 抽奖中球体半径变大时，平滑放大 */
  transition: transform 0.9s cubic-bezier(0.22, 0.8, 0.28, 1);
}
.pl__card {
  width: 112px;
  margin-left: -56px;
  margin-top: -78px;
  padding: 6px 6px 10px;
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(226, 232, 250, 0.95));
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.38);
  transform-origin: center;
  /* 与公转反向等速，照片正面始终朝屏幕 */
  animation: plFaceCounter var(--pl-spin) linear infinite;
}
.pl__card img {
  width: 100%;
  height: 128px;
  object-fit: cover;
  border-radius: 8px;
  display: block;
  background: #1e293b;
}
.pl__nick {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #3b2b6b;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@keyframes plWorldSpin {
  from { transform: rotateX(12deg) rotateY(0deg); }
  to { transform: rotateX(12deg) rotateY(360deg); }
}
@keyframes plFaceCounter {
  from { transform: rotateY(0deg) rotateX(-12deg); }
  to { transform: rotateY(-360deg) rotateX(-12deg); }
}
@keyframes plFloat {
  from { transform: translate(0, 0) scale(1); }
  to { transform: translate(3%, 4%) scale(1.12); }
}
@keyframes plHalo {
  from { opacity: 0.75; transform: translate(-50%, -50%) scale(1); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1.06); }
}
/* 抽奖中：球体保持 3D 球状，只加速 + 放大；用光圈高亮氛围（不用 filter，避免压平 3D） */
.pl__ring.is-hot {
  border-color: rgba(220, 170, 255, 0.55);
  box-shadow: inset 0 0 8vw rgba(160, 90, 255, 0.35), 0 0 5vw rgba(170, 90, 255, 0.35);
  animation: plHalo 1.2s ease-in-out infinite alternate;
}
.pl__drawing {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 40;
  padding: 12px 52px 16px;
  border-radius: 32px;
  text-align: center;
  pointer-events: none;
  white-space: nowrap;
  max-width: 92vw;
  /* 深色底衬，避免照片球的高亮卡片把文字吃掉 */
  background: rgba(12, 5, 28, 0.74);
  border: 2px solid rgba(200, 160, 255, 0.45);
  box-shadow: 0 0 48px rgba(150, 80, 255, 0.45), 0 12px 30px rgba(0, 0, 0, 0.5);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
}
.pl__drawing-text {
  display: inline-block;
  padding: 0 6px;
  line-height: 1.2;
  font-size: 64px;
  font-weight: 800;
  letter-spacing: 8px;
  background: linear-gradient(90deg, #ffd76a, #ff7ac8, #9f6bff);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: plPulse 0.9s ease-in-out infinite;
}
.pl__wait {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  text-align: center;
  pointer-events: none;
}
.pl__wait p {
  display: inline-block;
  padding: 10px 28px;
  border-radius: 999px;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 1px;
  color: #fff;
  background: rgba(10, 6, 20, 0.6);
  border: 1px solid rgba(190, 140, 255, 0.35);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.95);
}
.pl__reveal {
  position: absolute;
  left: 0;
  right: 0;
  /* 顶部让出横幅空间、底部让出人数信息，卡片只在中间区域居中 */
  top: 72px;
  bottom: 30px;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: plFadeIn 0.6s ease-out both;
}
.pl__winner {
  position: relative;
  width: 380px;
  padding: 18px 22px 22px;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: linear-gradient(180deg, rgba(120, 55, 200, 0.95), rgba(32, 12, 80, 0.95));
  border: 3px solid #ffd76a;
  box-shadow: 0 0 60px rgba(255, 200, 90, 0.55), 0 20px 60px rgba(0, 0, 0, 0.45);
  animation: plWinnerIn 0.7s cubic-bezier(0.2, 0.9, 0.3, 1.2) both;
}
.pl__winner-label {
  margin: 0 0 10px;
  font-size: 24px;
  line-height: 1.2;
  color: #ffd76a;
  letter-spacing: 4px;
}
.pl__winner img {
  width: 230px;
  height: 230px;
  /* 大屏高度有限时限制照片尺寸，避免卡片顶到顶部横幅 */
  max-width: 26vh;
  max-height: 26vh;
  object-fit: cover;
  border-radius: 18px;
  border: 3px solid rgba(255, 255, 255, 0.85);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
}
.pl__winner-nick {
  margin-top: 12px;
  font-size: 38px;
  line-height: 1.25;
  font-weight: 800;
  text-shadow: 0 0 18px rgba(255, 220, 120, 0.6);
}
.pl__winner-prize {
  margin-top: 6px;
  font-size: 20px;
  line-height: 1.3;
  color: #ffe6a5;
}
.pl__foot {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 18px;
  z-index: 20;
  text-align: center;
  font-size: 16px;
  color: #ede8ff;
  opacity: 1;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.95), 0 0 24px rgba(0, 0, 0, 0.8);
}
@keyframes plPulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.08); opacity: 0.85; }
}
@keyframes plFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes plWinnerIn {
  0% { opacity: 0; transform: scale(0.6); }
  100% { opacity: 1; transform: scale(1); }
}
</style>
